type LogLevel = "info" | "warn" | "error";

export type DebugLogEntry = {
  ts: string;
  level: LogLevel;
  tag: string;
  message: string;
  data?: unknown;
};

const MAX_ENTRIES = 80;
const STORAGE_KEY = "mvk_debug_logs";

let buffer: DebugLogEntry[] = [];

/** On-screen / buffered debug only in local DEV or explicit VITE_DEBUG_LOGS=true. Never auto-enable in MAX/prod. */
export function isDebugLogsEnabled(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_DEBUG_LOGS === "true";
}

function loadStored(): DebugLogEntry[] {
  if (!isDebugLogsEnabled()) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DebugLogEntry[];
    return Array.isArray(parsed) ? parsed.slice(-MAX_ENTRIES) : [];
  } catch {
    return [];
  }
}

function persist(): void {
  if (!isDebugLogsEnabled()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer.slice(-MAX_ENTRIES)));
  } catch {
    // ignore quota errors
  }
}

if (typeof window !== "undefined" && isDebugLogsEnabled()) {
  buffer = loadStored();
} else if (typeof window !== "undefined") {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function serializeValue(value: unknown): unknown {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      ...(value.cause !== undefined ? { cause: serializeValue(value.cause) } : {}),
    };
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      out[key] = serializeValue(entry);
    }
    return out;
  }
  return value;
}

function safeData(data: unknown): unknown {
  if (data === undefined) return undefined;
  try {
    return serializeValue(data);
  } catch {
    return String(data);
  }
}

export const debugLog = {
  info(tag: string, message: string, data?: unknown) {
    this.push("info", tag, message, data);
  },
  warn(tag: string, message: string, data?: unknown) {
    this.push("warn", tag, message, data);
  },
  error(tag: string, message: string, data?: unknown) {
    this.push("error", tag, message, data);
  },
  push(level: LogLevel, tag: string, message: string, data?: unknown) {
    if (!isDebugLogsEnabled()) return;
    const entry: DebugLogEntry = {
      ts: new Date().toISOString(),
      level,
      tag,
      message,
      data: safeData(data),
    };
    buffer = [...buffer.slice(-(MAX_ENTRIES - 1)), entry];
    persist();
    const fn = level === "error" ? console.error : level === "warn" ? console.warn : console.log;
    fn(`[mvk:${tag}]`, message, data ?? "");
  },
  getEntries(): DebugLogEntry[] {
    return [...buffer];
  },
  clear() {
    buffer = [];
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  },
  exportText(): string {
    return buffer
      .map((e) => {
        const data = e.data !== undefined ? ` ${JSON.stringify(e.data)}` : "";
        return `${e.ts} [${e.level}] ${e.tag}: ${e.message}${data}`;
      })
      .join("\n");
  },
};
